import {Box, Flex, P} from 'exsportia-components'
import {FC, FormEvent, useState} from 'react'
import styled from 'styled-components'

import {
  getPromoErrorFromResponse,
  isValidPromoCode,
  normalizePromoCode,
  PROMO_CODE_MAX_LENGTH,
} from '../../api/promo-codes/helpers'
import {useAddCode} from '../../api/promo-codes/my-promos'
import {PromoError} from '../../api/promo-codes/types'
import {usePromoText} from '../../components/promo-code-field/use-promo-text'
import {theme} from '../../theme/theme'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {TicketIcon} from './promo-card'

const Field = styled.label<{hasError: boolean}>`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding-inline-start: 14px;
  border-radius: 12px;
  border: 1.5px solid ${(props) => (props.hasError ? theme.colors.error : theme.colors.border)};
  background: ${theme.colors.bg};
  color: ${theme.colors['text-secondary']};
  cursor: text;

  &:focus-within {
    border-color: ${(props) => (props.hasError ? theme.colors.error : theme.colors.primary)};
    color: ${(props) => (props.hasError ? theme.colors.error : theme.colors.primary)};
  }
`

const CodeInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  padding-inline-end: 12px;
  border: none;
  background: transparent;
  font-family: inherit;
  font-size: 16px;
  text-transform: uppercase;
  color: ${theme.colors['text-default']};
  outline: none;

  &:not(:placeholder-shown) {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-weight: 600;
    letter-spacing: 0.08em;
  }

  &::placeholder {
    text-transform: none;
    color: ${theme.colors['text-secondary']};
  }
`

const BUTTON_SETTINGS = {width: 'max-content', height: '48px', borderRadius: '12px', px: '20px'}

// "Got a code? Add it." Saves a code the customer was given to their account for this venue.
export const AddCodeForm: FC<{venueId: string; currency?: string | null}> = ({venueId, currency}) => {
  const {l} = useLocale()
  const {errorMessage} = usePromoText(currency)
  const {mutateAsync, isLoading} = useAddCode()
  const [input, setInput] = useState('')
  const [error, setError] = useState<PromoError | null>(null)
  const [isDone, setIsDone] = useState(false)

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    const code = normalizePromoCode(input)
    setIsDone(false)
    if (!isValidPromoCode(code)) {
      setError({code: 'PROMO_NOT_FOUND'})
      return
    }
    setError(null)
    try {
      await mutateAsync({venueId, code})
      setInput('')
      setIsDone(true)
    } catch (e) {
      setError(getPromoErrorFromResponse(e) || {code: 'FALLBACK'})
    }
  }

  return (
    <Box width='100%'>
      <form onSubmit={(event) => void submit(event)}>
        <Flex
          gap='8px'
          alignItems='center'
        >
          <Field hasError={Boolean(error)}>
            <TicketIcon />
            <CodeInput
              value={input}
              maxLength={PROMO_CODE_MAX_LENGTH}
              autoComplete='off'
              autoCapitalize='characters'
              spellCheck={false}
              placeholder={l('vouchers_code_label')}
              aria-label={l('vouchers_code_label')}
              aria-invalid={Boolean(error)}
              onChange={(event) => {
                setInput(event.target.value.toUpperCase().slice(0, PROMO_CODE_MAX_LENGTH))
                setError(null)
                setIsDone(false)
              }}
            />
          </Field>
          <Box flexShrink={0}>
            <Button
              type={input ? 'primary' : 'secondary'}
              settings={BUTTON_SETTINGS}
              disabled={isLoading || !input}
              onClick={() => void submit()}
            >
              {isLoading ? l('vouchers_adding') : l('vouchers_add')}
            </Button>
          </Box>
        </Flex>
      </form>
      {error && (
        <Box
          mt='6px'
          role='alert'
        >
          <P color='error'>{errorMessage(error)}</P>
        </Box>
      )}
      {isDone && (
        <Box
          mt='6px'
          role='status'
        >
          <P color='success'>{l('vouchers_added')}</P>
        </Box>
      )}
    </Box>
  )
}
